import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import moment from 'moment';
import * as XLSX from 'xlsx';

import { DynamicListingComponent, FilterConfig, ColumnConfig, ActionConfig, ActionEvent, FilterEvent } from '../dynamic-listing/dynamic-listing';
import { ClientListService } from '../services/client/client-list.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { SVG_ICONS } from '../utils/svg-icons.utils';

interface ClientRow {
  id: string;
  client_code: string;
  created_at: string;
  client_name: string;
  category: string;
  type: string;
  phone: string;
  email: string;
}

@Component({
  selector: 'lib-client-list',
  standalone: true,
  imports: [CommonModule, DynamicListingComponent],
  templateUrl: './client-list.html',
  styleUrls: ['./client-list.css'],
})
export class ClientList implements OnInit {
  isLoading = false;
  exportFilename = `clients_${new Date().toISOString().slice(0, 10)}`;

  tableData: ClientRow[] = [];

  clientCreatedAt: Date | null = null;
  clientName = '';
  clientPhone = '';
  clientCategory = '';
  clientType: 'all' | 'draft' | 'non-draft' = 'all';
  referredBy = '';

  filterConfigs: FilterConfig[] = [];

  columnConfigs: ColumnConfig[] = [
    { key: 'client_code', header: 'Client Code' },
    { key: 'created_at', header: 'Created Date' },
    { key: 'client_name', header: 'Client Name', ellipsis: true },
    // { key: 'category', header: 'Category' },
    { key: 'type', header: 'Type' },
    { key: 'phone', header: 'Phone' },
    { key: 'email', header: 'Email', ellipsis: true },
    // { key: 'approval_status', header: 'Approval Status' },
  ];

  actionConfigs: ActionConfig[] = [
    {
      key: 'view',
      label: 'View',
      tooltip: 'View client',
      severity: 'secondary',
      svgIcon: SVG_ICONS.VIEW,
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit client',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT,
    },
    {
      key: 'ledger',
      label: 'Ledger',
      tooltip: 'View client ledger',
      severity: 'secondary',
      svgIcon: SVG_ICONS.LEDGER,
    },
    {
      key: 'delete',
      label: 'Delete',
      tooltip: 'Delete client',
      severity: 'secondary',
      svgIcon: SVG_ICONS.DELETE,
    },
  ];

  pageIndex = 1;
  rowsPerPage = 10;
  totalCount = 0;
  totalPages = 0;

  constructor(
    private readonly router: Router,
    private readonly clientService: ClientListService,
    private readonly toasterService: ToasterService
  ) {}

  async ngOnInit() {
    this.initializeFilterConfigs();
    await this.loadClients();
  }

  initializeFilterConfigs() {
    this.filterConfigs = [
      {
        key: 'clientCreatedAt',
        type: 'date',
        placeholder: 'Client Created Date',
        value: this.clientCreatedAt,
        showInAccordion: true,
      },
      {
        key: 'clientName',
        type: 'text',
        placeholder: 'Client Name',
        value: this.clientName,
        showInAccordion: true,
      },
      {
        key: 'clientPhone',
        type: 'text',
        placeholder: 'Phone No.',
        value: this.clientPhone,
        showInAccordion: true,
      }
    ];
  }

  async loadClients(page: number = 1) {
    try {
      this.isLoading = true;
      this.pageIndex = page;

      const filters = {
        type: this.clientType,
        limit: this.rowsPerPage,
        offset: (this.pageIndex - 1) * this.rowsPerPage,
        client_name: this.clientName.trim() || undefined,
        phone: this.clientPhone.trim() || undefined,
        category: this.clientCategory || undefined,
        referred_by: this.referredBy.trim() || undefined,
        is_deleted: false,
        created_at: this.clientCreatedAt
          ? moment(this.clientCreatedAt).format('YYYY-MM-DD')
          : undefined,
      } as const;

      const response = await lastValueFrom(this.clientService.getClients(filters));
      const clients = response?.data?.clients ?? [];

      this.tableData = clients.map((client: any) => {
        const basicDetails = client?.client_basic_details ?? {};
        const classification = client?.client_classification ?? {};
        const customFields = client?.custom_fields ?? {};

        return {
          id: client?.id ?? '',
          created_at: client?.created_at ? moment(client.created_at).format('DD/MM/YYYY') : '',
          client_code: client?.client_code ?? '',
          client_name: client?.client_name ?? '',
          category: client?.client_category ?? '',
          type: this.formatClientType(client?.client_type),
          phone:
            basicDetails?.client_phone ??
            client?.client_phone ??
            client?.client_contact?.contact_phone ??
            '',
          email:
            basicDetails?.client_email ??
            client?.client_email ??
            client?.client_contact?.contact_email ??
            '',
        };
      });

      this.totalCount = response?.data?.total ?? this.tableData.length;
      this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.rowsPerPage));
    } catch (error) {
      console.error('Error loading clients:', error);
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
      this.toasterService.error('Error', 'Failed to load clients');
    } finally {
      this.isLoading = false;
    }
  }

  onFilterEvent(event: FilterEvent) {
    this.clientCreatedAt = event.data?.clientCreatedAt ?? null;
    this.clientName = event.data?.clientName ?? '';
    this.clientPhone = event.data?.clientPhone ? String(event.data.clientPhone) : '';
    this.clientCategory = event.data?.clientCategory ?? '';
    this.clientType = (event.data?.clientType ?? 'all') as typeof this.clientType;
    this.referredBy = event.data?.referredBy ?? '';

    this.pageIndex = 1;
    this.loadClients(this.pageIndex);
  }

  onColumnsSelected(_: string[]) {
    // Future: persist column preferences
  }

  onActionClicked(event: ActionEvent) {
    const clientId = event.row?.id;
    if (!clientId) return;

    switch (event.action) {
      case 'view':
        this.view(clientId);
        break;
      case 'edit':
        this.edit(clientId);
        break;
      case 'ledger':
        this.viewLedger(clientId);
        break;
      case 'delete':
        this.delete(clientId);
        break;
    }
  }

  onPageChanged(page: number) {
    this.loadClients(page);
  }

  exportCsv() {
    if (!this.tableData.length) {
      this.toasterService.info('Info', 'No data available to export.');
      return;
    }

    const headers = [
      'S.No.',
      'Client Code',
      'Created Date',
      'Client Name',
      'Category',
      'Type',
      'Phone',
      'Email',
    ] as const;

    type ExportRow = { [K in (typeof headers)[number]]: string | number };

    const rows: ExportRow[] = this.tableData.map((row, index) => ({
      'S.No.': (this.pageIndex - 1) * this.rowsPerPage + index + 1,
      'Client Code': row.client_code ?? '',
      'Created Date': row.created_at ?? '',
      'Client Name': row.client_name ?? '',
      Category: row.category ?? '',
      Type: row.type ?? '',
      Phone: row.phone ?? '',
      Email: row.email ?? '',
    }));

    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        headers
          .map(header => {
            const cell = row[header] ?? '';
            const escaped = cell.toString().replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${this.exportFilename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  exportXlsx() {
    if (!this.tableData.length) {
      this.toasterService.info('Info', 'No data available to export.');
      return;
    }

    const headers = [
      'S.No.',
      'Client ID',
      'Created Date',
      'Client Name',
      'Category',
      'Type',
      'Sales Representative',
      'Phone',
      'Email',
      'Referred By',
      'Room No.',
    ] as const;

    const data = this.tableData.map((row, index) => ({
      'S.No.': (this.pageIndex - 1) * this.rowsPerPage + index + 1,
      'Client Code': row.client_code ?? '',
      'Created Date': row.created_at ?? '',
      'Client Name': row.client_name ?? '',
      Category: row.category ?? '',
      Type: row.type ?? '',
      Phone: row.phone ?? '',
      Email: row.email ?? '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = headers.map(() => ({ wch: 20 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients');
    XLSX.writeFile(workbook, `${this.exportFilename}.xlsx`);
  }

  exportPdf() {
    window.print();
  }

  view(clientId: string) {
    this.router.navigate(['/clients/onboarding'], {
      queryParams: { clientId, viewMode: true },
    });
  }

  edit(clientId: string) {
    console.log('clientId', clientId);
    // Find the client in tableData to check approval status
    const client = this.tableData.find(c => c.id === clientId);
    if (client && (client as any).approval_status === 'approved') {
      this.toasterService.warn('Warning', 'Cannot edit client with approved status');
      return;
    }
    this.router.navigate(['/clients/onboarding'], {
      queryParams: { clientId, viewMode: false },
    });
  }

  async delete(clientId: string) {
    if (!confirm('Are you sure you want to delete this client?')) {
      return;
    }

    try {
      this.isLoading = true;
      await lastValueFrom(this.clientService.deleteClient(clientId));
      this.toasterService.success('Success', 'Client deleted successfully');
      await this.loadClients(this.pageIndex);
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Failed to delete client';
      this.toasterService.error('Error', message);
    } finally {
      this.isLoading = false;
    }
  }

  viewLedger(clientId: string) {
    this.router.navigate(['/client-ledger', clientId]);
  }

  private formatClientType(value?: string): string {
    if (!value) {
      return '';
    }

    if (value === 'company' || value === 'individual' || value === 'partnership') {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }

    return value;
  }
}

